import Box from "@material-ui/core/Box";
import { makeStyles, Theme } from "@material-ui/core/styles";
import LinkIcon from "@material-ui/icons/Link";
import SettingsIcon from "@material-ui/icons/Settings";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import HistoricalTestCaseResponse from "../../../api/models/HistoricalTestCaseResponse";
import PagedResponse from "../../../api/models/PagedResponse";
import ProductType from "../../../api/models/product/ProductType";
import RequestsPagePathParams from "../../../api/models/test-request/RequestsPagePathParams";
import HistoricalTestCasesApi from "../../../api/services/HistoricalTestCasesApi";
import ProductSamplesApi from "../../../api/services/ProductSamplesApi";
import ReuseTestSeriesApi from "../../../api/services/ReusedTestSeriesApi";
import SubstrateSamplesApi from "../../../api/services/SubstrateSamplesApi";
import CustomProgress from "../../../components/Alert/CustomProgress";
import CustomButton from "../../../components/Button/CustomButton";
import { CustomTableRowData } from "../../../components/CustomTable";
import CustomDialog from "../../../components/Dialog/CustomDialog";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import { GalleryContextProvider } from "../../../contexts/GalleryContext";
import { ProductContext } from "../../../contexts/ProductContext";
import { SubstrateSampleContext } from "../../../contexts/SubstrateSampleContext";
import { getRowsPerPage, setRowsPerPage } from "../../../helper/UserSettingsHelper";
import useEnv from "../../../hooks/env";
import ProductSampleDetailsCompetitorDialog from "../ProductTab/ProductSampleDetailsCompetitorDialog";
import ProductSampleDetailsDialog from "../ProductTab/ProductSampleDetailsDialog";
import ProductSampleDetailsPrototypeDialog from "../ProductTab/ProductSampleDetailsPrototypeDialog";
import ProductSampleDetailsUnknownProductDialog from "../ProductTab/ProductSampleDetailsUnknownProductDialog";
import SubstrateDetailsDialog from "../SubstrateTab/SubstrateDetailsDialog";
import AnalyzeHistoricalResultsTable from "./AnalyzeHistoricalResultsTable";
import { TestRequestContext } from "../../../contexts/TestRequestContext";
import ProjectConfidentiality, {
  compareConfidentiality,
} from "../../../api/models/test-request/ProjectConfidentiality";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import TestRequestApi from "../../../api/services/TestRequestApi";
import { TestSeriesContext } from "../../../contexts/TestSeriesContext";
import createTmbActionIdObject from "./utils/CollectTmbIds";
import { AnalyzeHistoricalFormType } from "./AnalyzeHistoricalForm";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    "& > * + *": {
      marginTop: "1.5rem",
    },
  },
  gap: {
    gap: theme.spacing(3),
  },
  header: {
    textTransform: "uppercase",
    "& h2": {
      marginBottom: "0",
    },
  },
}));

type DialogType = ProductType | "SUBSTRATE";

const SETTINGS_BUTTON_LABEL = "Change Settings";
const REUSE_BUTTON_LABEL = "Reuse Data for Request";

const emptyActionIdObj = {
  productActionTmbIds: [],
  substrateActionTmbIds: [],
  buildingActionTmbIds: [],
  treatmentActionTmbIds: [],
};

function getEmptyResult(pageSize: number) {
  return {
    data: [],
    pageIndex: 0,
    pageSize,
    totalData: 0,
    totalPages: 0,
  };
}

interface AnalyzeHistoricalResultsDialogProps {
  onChangeSettings?: () => void;
  onClose: (doReload?: boolean) => void;
  open: boolean;
  testMethodName?: string;
  settingsCached: AnalyzeHistoricalFormType | null;
}

function AnalyzeHistoricalResultsDialog({
  onChangeSettings: handleChangeSettings,
  onClose: handleClose,
  open,
  testMethodName,
  settingsCached,
}: AnalyzeHistoricalResultsDialogProps) {
  const { api } = useEnv();
  const classes = useStyles();

  const { search } = useLocation();
  const { testRequestId: currentRequestId } = useParams<RequestsPagePathParams>();

  const { updateProductObject } = useContext(ProductContext);
  const { selectedTestSeriesObject } = useContext(TestSeriesContext);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { testRequestObj, updateTestRequestObj } = useContext(TestRequestContext);
  const { updateSubstrateId, updateSubstrateObject } = useContext(SubstrateSampleContext);
  const { handleHttpError, handleDialogNegativeCallback } = useContext(ErrorSnackbarContext);

  const testRequestApi = new TestRequestApi(api);
  const productSamplesApi = new ProductSamplesApi(api);
  const reusedTestSeriesApi = new ReuseTestSeriesApi(api);
  const substrateSamplesApi = new SubstrateSamplesApi(api);
  const historicalTestCasesApi = new HistoricalTestCasesApi(api);

  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsDialogType, setDetailsDialogType] = useState<DialogType | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(getRowsPerPage());
  const [sampleDetailsLoading, setSampleDetailsLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<
    { id: number; identifier: string; confidentiality: ProjectConfidentiality }[]
  >([]);
  const [testCasesPaged, setTestCasesPaged] = useState<PagedResponse<HistoricalTestCaseResponse>>(
    getEmptyResult(pageSize),
  );
  const [testRequestId, setTestRequestId] = useState<string | undefined>();

  const detailsDialogCallback = (closeDialog: boolean) => {
    setDetailsDialogOpen(!closeDialog);
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, page: number) => {
    setPageIndex(page);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const rowsPerPage = parseInt(event.target.value, 10);
    setPageSize(rowsPerPage);
    setRowsPerPage(rowsPerPage);
    setPageIndex(0);
  };

  const handleDetailsDialogClose = () => {
    setDetailsDialogOpen(false);
  };

  const handleProductCellClick = (sampleId: number, requestId: string) => {
    setSampleDetailsLoading(true);
    productSamplesApi
      .fetchProductSampleById(requestId, sampleId)
      .then(({ data }) => {
        updateProductObject(data);
        setTestRequestId(requestId);
        setDetailsDialogType(data.type);
        setDetailsDialogOpen(true);
      })
      .catch(handleHttpError)
      .finally(() => setSampleDetailsLoading(false));
  };

  const handleReuse = () => {
    const strictestConfidentiality = selectedRows
      .map((row) => row.confidentiality)
      .sort(compareConfidentiality)
      .shift();
    if (strictestConfidentiality && testRequestObj?.description?.projectConfidentiality) {
      if (compareConfidentiality(testRequestObj?.description?.projectConfidentiality, strictestConfidentiality) > 0) {
        const highly_ = strictestConfidentiality === "HIGHLY_CONFIDENTIAL" ? "highly " : "";
        const identifiers = selectedRows
          .filter((row) => row.confidentiality === strictestConfidentiality)
          .map((row) => row.identifier);
        return showConfirmationDialog({
          confirmText: `Set to ${highly_}confidential and continue`,
          text: `The original test ${identifiers.length > 1 ? "cases" : "case"} ${identifiers.join(", ")} ${
            identifiers.length > 1 ? "are" : "is"
          } ${highly_}confidential and can only be added if the request is set to ${highly_}confidential`,
          headline: `Set request to ${highly_}confidential`,
        })
          .then(() => testRequestApi.updateConfidentiality(currentRequestId, strictestConfidentiality))
          .then((response) => updateTestRequestObj(response.data))
          .then(() =>
            reusedTestSeriesApi.addReusedTestSeries(
              currentRequestId,
              selectedRows.map((row) => row.id),
            ),
          )
          .then(() => handleClose(true))
          .catch(handleDialogNegativeCallback);
      }
      return reusedTestSeriesApi
        .addReusedTestSeries(
          currentRequestId,
          selectedRows.map((row) => row.id),
        )
        .then(() => handleClose(true))
        .catch(handleHttpError);
    }
  };

  const handleRowSelectionChange = (checked: boolean, data: CustomTableRowData) => {
    if (checked) {
      setSelectedRows((prev) => {
        if (prev.map((row) => row.id).includes(data.id)) {
          return prev;
        }
        const testCase = testCasesPaged.data.filter((pagedTestCase) => pagedTestCase.id === data.id).pop();
        if (!testCase) {
          return prev;
        }
        return [
          ...prev,
          {
            id: data.id,
            identifier: `${testCase.testSeries.requestId}-${testCase.testSeries.indexNumber}-${testCase.indexNumber}`,
            confidentiality: testCase.testSeries.confidentiality,
          },
        ];
      });
    } else {
      setSelectedRows((prev) => {
        const index = prev.map((row) => row.id).indexOf(data.id);
        if (index >= 0) {
          const copy = [...prev];
          copy.splice(index, 1);
          return copy;
        }
        return prev;
      });
    }
  };

  const handleSelectAllChange = (checked: boolean, activeRows: HistoricalTestCaseResponse[]) => {
    activeRows.forEach((tc) => {
      handleRowSelectionChange(checked, { id: tc.id });
    });
  };

  const handleSubstrateCellClick = (sampleId: number, requestId: string) => {
    setSampleDetailsLoading(true);
    substrateSamplesApi
      .fetchSubstrateSampleById(requestId, sampleId)
      .then(({ data }) => {
        updateSubstrateId(sampleId);
        updateSubstrateObject(data);
        setTestRequestId(requestId);
        setDetailsDialogType("SUBSTRATE");
        setDetailsDialogOpen(true);
      })
      .catch(handleHttpError)
      .finally(() => setSampleDetailsLoading(false));
  };

  const titleTemplate = () => {
    const dialogTitle = "HISTORICAL DATA RESULTS";
    return testMethodName ? <h2>{`${dialogTitle} (${testMethodName})`}</h2> : <h2>{dialogTitle}</h2>;
  };

  useEffect(() => {
    if (open) {
      let doUpdate = true;
      setLoading(true);
      const tmbActionIdObj = search ? createTmbActionIdObject(selectedTestSeriesObject) : emptyActionIdObj;
      historicalTestCasesApi
        .searchHistoricalTestCases(currentRequestId, {
          filter: {
            ...tmbActionIdObj,
            productSampleIds: settingsCached?.productSamples.map((productSample) => productSample.id) || [],
            substrateSampleIds: settingsCached?.substrateSamples.map((substrateSample) => substrateSample.id) || [],
            testMethodReferences: settingsCached?.testMethods.map(({ id, version }) => ({ id, version })) || [],
          },
          pagination: {
            pageIndex,
            pageSize,
          },
        })
        .then(({ data }) => {
          if (doUpdate) {
            setTestCasesPaged(data);
          }
        })
        .catch((e) => {
          handleHttpError(e);
          if (doUpdate) {
            setTestCasesPaged(getEmptyResult(pageSize));
          }
        })

        .finally(() => {
          setInitializing(false);
          setLoading(false);
        });
      return () => {
        doUpdate = false;
      };
    }
  }, [currentRequestId, open, pageIndex, pageSize, settingsCached]);

  useEffect(() => {
    if (!open) {
      return () => setSelectedRows([]);
    }
  }, [open]);

  if (open && initializing) {
    return <CustomProgress variant="viewportCentered" />;
  }

  return (
    <CustomDialog fullWidth maxWidth="xl" onClose={handleClose} open={open}>
      <Box className={classes.container}>
        {titleTemplate()}
        <Box className={classes.gap} display="flex" justifyContent="right">
          {handleChangeSettings && (
            <CustomButton onClick={handleChangeSettings} startIcon={<SettingsIcon />} text={SETTINGS_BUTTON_LABEL} />
          )}
          <CustomButton onClick={handleReuse} startIcon={<LinkIcon />} text={REUSE_BUTTON_LABEL} />
        </Box>
        <AnalyzeHistoricalResultsTable
          loading={loading}
          onProductCellClick={handleProductCellClick}
          onRowSelectionChange={handleRowSelectionChange}
          onSelectAllChange={handleSelectAllChange}
          onSubstrateCellClick={handleSubstrateCellClick}
          results={testCasesPaged.data}
          selectedRows={selectedRows.reduce((a, b) => {
            a[b.id] = true;
            return a;
          }, {} as Record<number, boolean>)}
          TablePaginationProps={{
            count: testCasesPaged.totalData || 0,
            onChangePage: handleChangePage,
            onChangeRowsPerPage: handleChangeRowsPerPage,
            page: pageIndex,
            rowsPerPage: pageSize,
          }}
        />
      </Box>
      <GalleryContextProvider>
        <ProductSampleDetailsDialog
          testRequestID={testRequestId || ""}
          onClose={handleDetailsDialogClose}
          open={detailsDialogOpen && detailsDialogType === "TESA_PRODUCT"}
          callback={detailsDialogCallback}
        />
      </GalleryContextProvider>
      <GalleryContextProvider>
        <ProductSampleDetailsPrototypeDialog
          apiReqType="POST"
          testRequestID={testRequestId || ""}
          onClose={handleDetailsDialogClose}
          open={detailsDialogOpen && detailsDialogType === "TESA_PROTOTYPE"}
          callback={detailsDialogCallback}
        />
      </GalleryContextProvider>
      <GalleryContextProvider>
        <ProductSampleDetailsCompetitorDialog
          testRequestID={testRequestId || ""}
          onClose={handleDetailsDialogClose}
          open={detailsDialogOpen && detailsDialogType === "COMPETITOR_PRODUCT"}
          callback={detailsDialogCallback}
        />
      </GalleryContextProvider>
      <GalleryContextProvider>
        <ProductSampleDetailsUnknownProductDialog
          testRequestID={testRequestId || ""}
          onClose={handleDetailsDialogClose}
          open={detailsDialogOpen && detailsDialogType === "UNKNOWN_PRODUCT"}
          callback={detailsDialogCallback}
        />
      </GalleryContextProvider>
      <GalleryContextProvider>
        <SubstrateDetailsDialog
          callback={detailsDialogCallback}
          onClose={handleDetailsDialogClose}
          open={detailsDialogOpen && detailsDialogType === "SUBSTRATE"}
          testRequestId={testRequestId}
        />
      </GalleryContextProvider>
      {sampleDetailsLoading && <CustomProgress variant="viewportCentered" />}
    </CustomDialog>
  );
}

export default AnalyzeHistoricalResultsDialog;
